import React, { useState, useCallback } from 'react';
import AltifierImageCard from './AltifierImageCard';
import BlueskyPostEmbed from './BlueskyPostEmbed';

export default function AltifierPostCard({ post, sessionDid, onApply, geminiEnabled }) {
  const [approved, setApproved] = useState({});
  const [rejected, setRejected] = useState(new Set());
  const [isApplying, setIsApplying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [applyError, setApplyError] = useState(null);

  const missingImages = post.images.filter((img) => !img.hasAlt);

  const handleApproveImage = useCallback((imageIndex, altText) => {
    setApproved((prev) => ({ ...prev, [imageIndex]: altText }));
    setRejected((prev) => {
      const next = new Set(prev);
      next.delete(imageIndex);

      return next;
    });
  }, []);

  const handleRejectImage = useCallback((imageIndex) => {
    setRejected((prev) => new Set(prev).add(imageIndex));
    setApproved((prev) => {
      const next = { ...prev };
      delete next[imageIndex];

      return next;
    });
  }, []);

  const approvedCount = Object.keys(approved).length;

  const allReviewed =
    missingImages.length === 0 ||
    (approvedCount + rejected.size >= missingImages.length);

  // P7 — Build the old → new diff shown before applying.
  const diffEntries = Object.entries(approved).map(([idx, newText]) => {
    const i = parseInt(idx, 10);

    const img = post.images.find((m) => m.index === i) || {
      index: i,
      kind: 'image',
      currentAlt: '',
    };

    const oldAlt = img.currentAlt ? `"${img.currentAlt}"` : '(empty)';
    const label = img.kind === 'video' ? 'Video' : `Image ${img.index + 1}`;

    return { label, oldAlt, newText };
  });

  const handleApplyClick = () => {
    setShowConfirm(true);
    setApplyError(null);
  };

  const handleConfirmApply = async () => {
    setIsApplying(true);
    setShowConfirm(false);
    setApplyError(null);

    try {
      await onApply(post, approved);
    } catch (err) {
      setApplyError(err.message || 'Failed to apply alt text');
    } finally {
      setIsApplying(false);
    }
  };

  const imagesToReview = missingImages.length > 0 ? missingImages : post.images;

  return (
    <div className="altifier-post-card">
      <div className="altifier-post-context">
        <p className="altifier-post-text">
          {post.text || '(no text)'}
        </p>
        <div className="altifier-post-meta">
          <span>{new Date(post.createdAt).toLocaleDateString()}</span>
          {post.isReply && (
            <span className="altifier-post-reply-badge">Reply</span>
          )}
        </div>
        <div className="altifier-post-aturi" title="Post AT-URI">
          <code>{post.uri}</code>
        </div>
        <BlueskyPostEmbed did={sessionDid} rkey={post.rkey} />
      </div>

      <div className="altifier-post-images">
        {imagesToReview.map((img) => (
          <AltifierImageCard
            key={img.index}
            image={img}
            postText={post.text}
            onApprove={handleApproveImage}
            onReject={handleRejectImage}
            isApplying={isApplying}
            geminiEnabled={geminiEnabled}
          />
        ))}
      </div>

      {missingImages.length > 0 && (
        <div className="altifier-post-footer">
          <div className="altifier-review-status">
            {approvedCount} of {missingImages.length} image{missingImages.length !== 1 ? 's' : ''} approved
            {rejected.size > 0 && `, ${rejected.size} skipped`}
            {allReviewed && approvedCount > 0 && ' — ready to apply'}
          </div>

          {approvedCount > 0 && (
            <button
              onClick={handleApplyClick}
              disabled={isApplying}
              className="altifier-apply-button"
            >
              {isApplying ? 'Applying...' : `Apply ${approvedCount} Alt Text${approvedCount !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      )}

      {applyError && (
        <div className="altifier-status-box altifier-status-box-error">
          <p>{applyError}</p>
        </div>
      )}

      {showConfirm && (
        <div className="altifier-modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="altifier-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Alt Text Update</h3>
            <p>
              Alt text will be applied by <strong>deleting this post and
              recreating it</strong> with the new alt text. The original post&apos;s
              likes, reposts, quotes, and replies will be <strong>lost
              permanently</strong>. (AT Protocol posts are immutable; Bluesky
              ignores in-place edits.)
            </p>
            <ul className="altifier-modal-diff">
              {diffEntries.map((d) => (
                <li key={d.label}>
                  <strong>{d.label}:</strong> <span className="altifier-diff-old">{d.oldAlt}</span>
                  {' → '}
                  <span className="altifier-diff-new">"{d.newText}"</span>
                </li>
              ))}
            </ul>
            <div className="altifier-modal-buttons">
              <button
                onClick={handleConfirmApply}
                className="altifier-modal-confirm"
              >
                Apply Changes
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="altifier-modal-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
